import { ChevronLeft, ChevronRight, KeyboardArrowDown } from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { forwardRef, useMemo, useState } from "react";
import type { DateFilterType } from "@/utils/interfaces";

type PeriodFilterType = Extract<DateFilterType, "day" | "week" | "month">;

export interface CompactPeriodControlProps {
  selectedDate: Dayjs;
  dateFilterType: string;
  label: string;
  accentColor: string;
  onDateChange: (date: Dayjs) => void;
  onFilterTypeChange: (type: DateFilterType) => void;
  onResetPeriod: () => void;
  disabled?: boolean;
}

function toPeriodFilterType(dateFilterType: string): PeriodFilterType {
  if (dateFilterType === "day" || dateFilterType === "week") return dateFilterType;
  return "month";
}

export const CompactPeriodControl = forwardRef<HTMLDivElement, CompactPeriodControlProps>(
  function CompactPeriodControl(
    {
      selectedDate,
      dateFilterType,
      label,
      accentColor,
      onDateChange,
      onFilterTypeChange,
      onResetPeriod,
      disabled = false,
    },
    ref
  ) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const filterType = toPeriodFilterType(dateFilterType);

    const calendarViews = useMemo(
      () => (filterType === "month" ? (["year", "month"] as const) : (["day"] as const)),
      [filterType]
    );

    const stepPrevious = () => {
      onDateChange(selectedDate.subtract(1, filterType));
    };

    const stepNext = () => {
      onDateChange(selectedDate.add(1, filterType));
    };

    const hoverBg = alpha(accentColor, 0.08);

    return (
      <Stack
        ref={ref}
        direction="row"
        alignItems="center"
        spacing={0.25}
        sx={{ maxWidth: "100%" }}
      >
        <IconButton
          size="small"
          disabled={disabled}
          aria-label="Período anterior"
          onClick={stepPrevious}
          sx={{ "&:hover": { bgcolor: hoverBg } }}
        >
          <ChevronLeft />
        </IconButton>

        <Button
          disabled={disabled}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          endIcon={<KeyboardArrowDown />}
          sx={{
            color: "text.primary",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            lineHeight: 1.3,
            px: 1,
            minWidth: 0,
            maxWidth: { xs: 200, sm: 280 },
            "&:hover": { bgcolor: hoverBg },
            "& .MuiButton-endIcon": { ml: 0.25 },
          }}
        >
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {label}
          </Box>
        </Button>

        <IconButton
          size="small"
          disabled={disabled}
          aria-label="Próximo período"
          onClick={stepNext}
          sx={{ "&:hover": { bgcolor: hoverBg } }}
        >
          <ChevronRight />
        </IconButton>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Stack spacing={1.5} sx={{ p: 2, width: 320, maxWidth: "100vw" }}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={filterType}
              onChange={(_, value: PeriodFilterType | null) => {
                if (value) onFilterTypeChange(value);
              }}
            >
              <ToggleButton value="day">Dia</ToggleButton>
              <ToggleButton value="week">Semana</ToggleButton>
              <ToggleButton value="month">Mês</ToggleButton>
            </ToggleButtonGroup>

            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
              <DateCalendar
                value={selectedDate}
                views={calendarViews}
                openTo={filterType === "month" ? "month" : "day"}
                onChange={(value) => {
                  if (value) {
                    onDateChange(value);
                    setAnchorEl(null);
                  }
                }}
              />
            </LocalizationProvider>

            <Button
              variant="text"
              onClick={() => {
                onResetPeriod();
                setAnchorEl(null);
              }}
              sx={{ alignSelf: "flex-start", fontWeight: 600 }}
            >
              Voltar ao mês atual
            </Button>
          </Stack>
        </Popover>
      </Stack>
    );
  }
);
